import { useState, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faLanguage,
  faExclamationCircle,
} from "@fortawesome/pro-light-svg-icons";
import classNames from "classnames";
import TranslationPopover from "./TranslationPopover";
import MarkdownEditor from "../markdownEditor/MarkdownEditor";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/components/ui/popover";

interface TranslatableMarkdownEditorProps {
  /** The current value of the editor (typically the default language value) */
  value: string;

  /** Callback when the main editor value changes */
  onChange?: (value: string) => void;

  /**
   * The translations object containing all language translations.
   * Format: { "en": { "fieldName": "value" }, "de": { "fieldName": "value" } }
   */
  translationsValue: Record<string, Record<string, string>>;

  /** Callback when translations are updated. */
  onTranslationsChange?: (
    translations: Record<string, Record<string, string>>,
  ) => void;

  /**
   * Unified callback that provides both the main value and translations atomically.
   * When provided, handles bidirectional sync internally:
   * - Main editor changes sync to translations[defaultLocale][field]
   * - Popover changes sync translations[defaultLocale][field] back to the main value
   */
  onValueChange?: (
    value: string,
    translations: Record<string, Record<string, string>>,
  ) => void;

  /** The field name being translated (e.g., "teaser", "description") */
  field: string;

  /** Whether the editor is disabled */
  disabled?: boolean;

  /** Optional placeholder for the main editor */
  placeholder?: string;

  /** Whether to enable tixxt mentions in the editor */
  enableTixxtMentions?: boolean;

  /** Side where the popover should appear (default: "left") */
  popoverSide?: "top" | "right" | "bottom" | "left";

  /** Alignment of the popover relative to the trigger (default: "start") */
  popoverAlign?: "start" | "center" | "end";

  /**
   * Makes the main editor taller and freely resizable (auto-grow + drag handle).
   * Forwarded to MarkdownEditor; the translation popover editors stay compact.
   */
  resizable?: boolean;
}

/**
 * Markdown editor with integrated multilingual translation support.
 *
 * Mirrors TranslatableTextInput but uses MarkdownEditor for both the main
 * control and the per-language controls inside the translation popover.
 */
export default function TranslatableMarkdownEditor({
  value,
  onChange,
  translationsValue,
  onTranslationsChange,
  onValueChange,
  field,
  disabled = false,
  placeholder,
  enableTixxtMentions,
  popoverSide = "left",
  popoverAlign = "start",
  resizable = false,
}: TranslatableMarkdownEditorProps) {
  const [isOpen, setIsOpen] = useState(false);

  const defaultLocale = Preload.current_network?.language?.split("_")[0];
  const multilingualismEnabled =
    Preload.current_network?.config.multilingualism.enabled;

  const availableLanguages = useMemo(
    () =>
      Preload.current_network?.config.multilingualism.available_languages || [],
    [],
  );

  const hasMissingTranslations = useMemo(() => {
    if (!translationsValue) return false;

    return availableLanguages.some((lang) => {
      const translation = translationsValue[lang]?.[field];
      return !translation || translation.trim() === "";
    });
  }, [translationsValue, availableLanguages, field]);

  const handleEditorChange = (newValue: string) => {
    if (onValueChange) {
      const syncedTranslations =
        defaultLocale && multilingualismEnabled
          ? {
              ...translationsValue,
              [defaultLocale]: {
                ...(translationsValue[defaultLocale] || {}),
                [field]: newValue,
              },
            }
          : translationsValue;
      onValueChange(newValue, syncedTranslations);
    } else {
      onChange?.(newValue);
    }
  };

  const handleTranslationsChange = (
    translations: Record<string, Record<string, string>>,
  ) => {
    if (onValueChange) {
      const syncedValue =
        defaultLocale && translations[defaultLocale]?.[field] !== undefined
          ? translations[defaultLocale][field]
          : value;
      onValueChange(syncedValue, translations);
    } else {
      onTranslationsChange?.(translations);
    }
  };

  const mainEditor = (
    <div
      // may shrink below its content, so a long link wraps inside the editor
      // instead of widening it past the form next to the translation button
      // (tixxt/core#712)
      className={classNames("grow min-w-0", {
        // When resizable, let MarkdownEditor's own min/max height drive sizing
        // instead of pinning the wrapper to a fixed height.
        "h-48": !resizable,
        "h-90": window.isApp,
      })}
    >
      <MarkdownEditor
        value={value}
        onChange={handleEditorChange}
        readOnly={disabled}
        placeholder={placeholder}
        enableTixxtMentions={enableTixxtMentions}
        resizable={resizable}
      />
    </div>
  );

  if (!multilingualismEnabled) {
    return mainEditor;
  }

  return (
    <div className="flex items-start gap-2">
      {mainEditor}

      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="btn btn-sm btn-link p-1"
            data-testid="translation-toggle"
            disabled={disabled}
            style={{
              position: "relative",
              lineHeight: 1,
              opacity: disabled ? 0.5 : 1,
              cursor: disabled ? "not-allowed" : "pointer",
            }}
          >
            <FontAwesomeIcon
              icon={faLanguage}
              className={
                hasMissingTranslations ? "text-danger" : "text-success"
              }
              size="lg"
            />
            {hasMissingTranslations && (
              <FontAwesomeIcon
                icon={faExclamationCircle}
                className="text-danger"
                style={{
                  position: "absolute",
                  top: "-4px",
                  right: "-4px",
                  fontSize: "0.7em",
                  backgroundColor: "white",
                  borderRadius: "50%",
                }}
              />
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent
          align={popoverAlign}
          side={popoverSide}
          className="w-auto p-4"
        >
          <TranslationPopover
            field={field}
            translationsValue={translationsValue}
            onTranslationsChange={handleTranslationsChange}
            onClose={() => setIsOpen(false)}
            wide
            renderInput={({ value: langValue, onChange: onLangChange }) => (
              <div className={classNames("h-48", { "h-90": window.isApp })}>
                <MarkdownEditor
                  value={langValue}
                  onChange={onLangChange}
                  readOnly={disabled}
                  placeholder={placeholder}
                  enableTixxtMentions={enableTixxtMentions}
                />
              </div>
            )}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
